/**
 * Token Usage Analytics dialog (agent-facing).
 *
 * Shows an aggregated dashboard of every share-link recipient's token spend
 * for the calling agent, plus a per-client drill-down. Backed by the
 * `/api/super-picker-analytics/agent/*` endpoints which scope every read by
 * `InvitedBy = req.user.cea`.
 *
 * Layout:
 *   ┌── Agent overview ──────────────────────────────────────────────┐
 *   │ Date range │ Refresh                                           │
 *   │ ┌── Headline cards ──┐ ┌── Section breakdown ─┐ ┌── Spend/day ┐│
 *   │ ┌── Top projects ────────┐ ┌── Top blocks ─┐ ┌── Top units ─┐  │
 *   │ Search clients [_______]  Filtered list                         │
 *   │ Click a client → drill-down view                                │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * The drill-down replaces the overview body with a "back" button + the
 * client's own headline numbers, project / block / section breakdowns, and
 * a recent-events table. The same date range applies to both views.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Toll as TokenIcon,
} from '@mui/icons-material';
import {
  api,
  TokenUsageAdminOverviewResponse,
  TokenUsageAgentSummary,
  TokenUsageClientDetailResponse,
  TokenUsageClientSearchRow,
  TokenUsageOverviewResponse,
} from '../../services/api';
import {
  DateRangeSelector,
  DateRangeState,
  DEFAULT_DATE_RANGE_PRESETS,
  DateRangePreset,
  formatInputDate,
  parseInputDate,
  calendarToday,
  addDaysDate,
  isBeforeDate,
} from './DateRangeSelector';

interface TokenUsageAnalyticsDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * `'agent'` (default) hits the agent endpoints which the backend scopes
   * to `req.user.cea`. `'admin'` hits the admin endpoints, surfacing a
   * Top Agents panel and an `agentCea` filter chip. Admin mode is gated
   * server-side by `requireAdmin` regardless of this prop.
   */
  mode?: 'agent' | 'admin';
  /**
   * When true the dialog renders without its outer `Dialog` chrome — used
   * by `AnalyticsDashboardDialog`'s "Token Usage" tab so the same component
   * can be embedded inline alongside the existing analytics tabs.
   */
  embedded?: boolean;
}

// "All time" preset matches the existing analytics-dashboard convention
// (1 Mar 2026 lower bound, present-day upper bound). Keeps the agent and
// admin date pickers visually consistent.
const ANALYTICS_ALL_TIME_START = '2026-03-01';

function createAllTimeRange(): DateRangeState {
  const today = calendarToday();
  const start = parseInputDate(ANALYTICS_ALL_TIME_START);
  const end = start && isBeforeDate(today, start) ? start : today;
  return {
    startDate: ANALYTICS_ALL_TIME_START,
    endDate: formatInputDate(end),
  };
}

const ANALYTICS_DATE_PRESETS: DateRangePreset[] = [
  ...DEFAULT_DATE_RANGE_PRESETS,
  { id: 'allTime', label: 'All time', build: () => createAllTimeRange() },
];

const ROWS_PER_PAGE = 5;

const MODULE_LABEL: Record<string, string> = {
  PICKER_SITE_PLANS: 'Site Plans',
  PICKER_PROJECT_DETAILS: 'Project Details',
  PICKER_PROJECT_RESEARCH: 'Project Research',
};
function formatModule(module: string | null | undefined): string {
  if (!module) return '—';
  return MODULE_LABEL[module] ?? module;
}

function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-SG', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-SG', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-SG').format(n);
}

// ── Subcomponents ────────────────────────────────────────────────────

/**
 * Card chrome shared with `AnalyticsDashboardDialog` (kept in sync with its
 * `chartCardSx` so the embedded Token Usage tab visually matches the rest
 * of the dashboard).
 */
const chartCardSx = {
  p: 2.5,
  bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
  border: '1px solid rgba(var(--color-text-rgb), 0.08)',
  borderRadius: 2,
  height: '100%',
} as const;

/**
 * Filter row above the body content. Mirrors the byDate / overview tabs in
 * `AnalyticsDashboardDialog`: a flex container of pickers / chips aligned
 * to the bottom edge.
 */
const FILTER_ROW_SX = {
  display: 'flex',
  gap: 2,
  flexWrap: 'wrap',
  alignItems: 'flex-end',
} as const;

/**
 * Headline KPI card. Matches the layout used by the byDate / overview tabs
 * of the analytics dashboard (overline label, h5 value, body2 hint). Render
 * inside a `Grid item xs sm={6} md={3}` for a 4-up row.
 */
const HeadlineCard: React.FC<{ label: string; value: string; hint?: string }> = ({
  label, value, hint,
}) => (
  <Card sx={chartCardSx}>
    <CardContent sx={{ p: '0 !important' }}>
      <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
          {hint}
        </Typography>
      )}
    </CardContent>
  </Card>
);

interface SimpleSparklineProps {
  data: Array<{ date: string; tokens: number }>;
  height?: number;
}

/**
 * Tiny inline sparkline implemented in raw SVG to avoid pulling another
 * chart lib into this dialog. Each datum is plotted as a vertical bar; we
 * re-scale the y-axis to the max value in the window. Empty data → no SVG.
 */
const SimpleSparkline: React.FC<SimpleSparklineProps> = ({ data, height = 60 }) => {
  if (data.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
        No spend in this period.
      </Typography>
    );
  }
  const max = Math.max(...data.map((d) => d.tokens), 1);
  const barWidth = 100 / data.length;
  return (
    <Box sx={{ width: '100%' }}>
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
        {data.map((d, idx) => {
          const h = (d.tokens / max) * (height - 2);
          return (
            <rect
              key={d.date}
              x={idx * barWidth + 0.2}
              y={height - h - 1}
              width={Math.max(barWidth - 0.4, 0.5)}
              height={h}
              fill="rgba(237,137,54,0.85)"
            >
              <title>{`${d.date}: ${d.tokens} token${d.tokens === 1 ? '' : 's'}`}</title>
            </rect>
          );
        })}
      </svg>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
          {data[0].date}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
          {data[data.length - 1].date}
        </Typography>
      </Box>
    </Box>
  );
};

const SectionBreakdownPanel: React.FC<{
  data: { projectDetails: number; projectResearch: number; unknown: number };
}> = ({ data }) => {
  const total = data.projectDetails + data.projectResearch + data.unknown;
  const rows: Array<{ label: string; value: number; color: string }> = [
    { label: 'Project details', value: data.projectDetails, color: '#4299E1' },
    { label: 'Project research', value: data.projectResearch, color: '#9F7AEA' },
    { label: 'Other', value: data.unknown, color: 'rgba(var(--color-text-rgb), 0.4)' },
  ];
  return (
    <Stack spacing={1.25}>
      {rows.map((row) => {
        const pct = total > 0 ? Math.round((row.value / total) * 100) : 0;
        return (
          <Box key={row.label}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {row.label}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                {formatNumber(row.value)} ({pct}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={total === 0 ? 0 : (row.value / total) * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'rgba(var(--color-text-rgb), 0.08)',
                '& .MuiLinearProgress-bar': { bgcolor: row.color, borderRadius: 3 },
              }}
            />
          </Box>
        );
      })}
    </Stack>
  );
};

const PanelTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25, color: 'var(--color-heading)' }}>
    {children}
  </Typography>
);

const Panel: React.FC<{ children: React.ReactNode; flex?: string }> = ({ children, flex }) => (
  <Box
    sx={{
      flex: flex ?? '1 1 320px',
      minWidth: 280,
      p: 2,
      borderRadius: 2,
      border: '1px solid rgba(var(--color-text-rgb), 0.08)',
      bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
    }}
  >
    {children}
  </Box>
);

// ── Main dialog ──────────────────────────────────────────────────────

const TokenUsageAnalyticsDialog: React.FC<TokenUsageAnalyticsDialogProps> = ({
  open,
  onClose,
  mode = 'agent',
  embedded = false,
}) => {
  const isAdmin = mode === 'admin';
  const [range, setRange] = useState<DateRangeState>(() => createAllTimeRange());
  // Admin-only: scope every fetch to a single agent's CEA. `null` ⇒ the
  // cross-agent view. Ignored entirely in agent mode.
  const [selectedAgent, setSelectedAgent] = useState<TokenUsageAgentSummary | null>(null);
  // Admin-only: agent picker autocomplete state. `agentInput` is the
  // current text in the Autocomplete — we debounce it through
  // `searchSuperPickerAdminAgents` to populate `agentOptions`.
  const [agentInput, setAgentInput] = useState('');
  const [agentOptions, setAgentOptions] = useState<TokenUsageAgentSummary[]>([]);
  const [agentOptionsLoading, setAgentOptionsLoading] = useState(false);
  // The admin endpoint returns the same shape as the agent endpoint plus a
  // `topAgents` rollup. We over-type to the wider response so admin-only
  // fields are accessible without casts in the JSX path.
  const [overview, setOverview] = useState<TokenUsageAdminOverviewResponse | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<TokenUsageClientSearchRow[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // When a client is selected the dialog shows the drill-down panel
  // instead of the overview. `null` ⇒ overview view. The key is the
  // backend's `clientKey` (normalized mobile or legacy `eu:<id>`), so
  // multiple share links to the same person collapse into one drill-down.
  const [selectedClientKey, setSelectedClientKey] = useState<string | null>(null);
  const [clientDetail, setClientDetail] = useState<TokenUsageClientDetailResponse | null>(null);
  const [clientDetailLoading, setClientDetailLoading] = useState(false);
  const [clientDetailError, setClientDetailError] = useState<string | null>(null);

  // Fetch the overview whenever the date range or dialog open state
  // changes. `range` is the only knob the agent can twiddle; the agent
  // identifier is implied by the auth header.
  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const agentCea = selectedAgent?.ceaNumber;
      const data = isAdmin
        ? await api.getSuperPickerAdminAnalyticsOverview({
            startDate: range.startDate, endDate: range.endDate, agentCea,
          })
        : await api.getSuperPickerAgentAnalyticsOverview({
            startDate: range.startDate, endDate: range.endDate,
          });
      // Agent endpoint omits `topAgents`; coerce to the wider shape with an
      // empty array so the rest of the dialog can read it uniformly.
      const widened: TokenUsageAdminOverviewResponse = 'topAgents' in data
        ? (data as TokenUsageAdminOverviewResponse)
        : { ...(data as TokenUsageOverviewResponse), topAgents: [] };
      setOverview(widened);
    } catch (err) {
      setOverviewError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setOverviewLoading(false);
    }
  }, [range.startDate, range.endDate, isAdmin, selectedAgent?.ceaNumber]);

  // Fetch the client detail for the selected drill-down. Re-runs when the
  // selected client OR the date range changes.
  const fetchClientDetail = useCallback(async (clientKey: string) => {
    setClientDetailLoading(true);
    setClientDetailError(null);
    try {
      const agentCea = selectedAgent?.ceaNumber;
      const data = isAdmin
        ? await api.getSuperPickerAdminClientDetail(clientKey, {
            startDate: range.startDate, endDate: range.endDate, agentCea,
          })
        : await api.getSuperPickerAgentClientDetail(clientKey, {
            startDate: range.startDate, endDate: range.endDate,
          });
      setClientDetail(data);
    } catch (err) {
      setClientDetailError(err instanceof Error ? err.message : 'Failed to load client');
    } finally {
      setClientDetailLoading(false);
    }
  }, [range.startDate, range.endDate, isAdmin, selectedAgent?.ceaNumber]);

  // Reset state on every open so reopening the dialog never shows stale
  // numbers from a previous session.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSearchResults(null);
    setSelectedClientKey(null);
    setClientDetail(null);
    void fetchOverview();
  }, [open, fetchOverview]);

  // Re-fetch the drill-down whenever the date range changes while a
  // client is selected. Skips the re-fetch when no client is open.
  useEffect(() => {
    if (selectedClientKey == null) return;
    void fetchClientDetail(selectedClientKey);
  }, [selectedClientKey, fetchClientDetail]);

  // Debounced client search. Empty query → default browse list (limit 50,
  // newest-first) which is also useful before the user types anything.
  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = isAdmin
          ? await api.searchSuperPickerAdminClients(search, { agentCea: selectedAgent?.ceaNumber })
          : await api.searchSuperPickerAgentClients(search);
        setSearchResults(data.clients);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [open, search, isAdmin, selectedAgent?.ceaNumber]);

  // Debounced agent search for the admin agent-picker Autocomplete. Hits
  // the same endpoint as the byDate user search so spelling-tolerant
  // lookups by name or CEA number work consistently.
  useEffect(() => {
    if (!open || !isAdmin) return;
    const handle = window.setTimeout(async () => {
      setAgentOptionsLoading(true);
      try {
        const data = await api.searchSuperPickerAdminAgents(agentInput, {
          startDate: range.startDate,
          endDate: range.endDate,
        });
        setAgentOptions(data.agents);
      } catch {
        setAgentOptions([]);
      } finally {
        setAgentOptionsLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [open, isAdmin, agentInput, range.startDate, range.endDate]);

  const handleSelectClient = useCallback((clientKey: string) => {
    setSelectedClientKey(clientKey);
  }, []);

  const handleBackToOverview = useCallback(() => {
    setSelectedClientKey(null);
    setClientDetail(null);
    setClientDetailError(null);
  }, []);

  // Compact header actions — just refresh / close. The date range picker
  // and admin scope chip live in the dedicated filter row below the title
  // (mirrors how byDate / overview tabs render in AnalyticsDashboardDialog).
  const headerActions = useMemo(() => (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Tooltip title="Refresh">
        <span>
          <IconButton
            size="small"
            onClick={() => {
              if (selectedClientKey != null) void fetchClientDetail(selectedClientKey);
              else void fetchOverview();
            }}
            disabled={overviewLoading || clientDetailLoading}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {!embedded && (
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>
  ), [selectedClientKey, fetchOverview, fetchClientDetail, overviewLoading, clientDetailLoading, onClose, embedded]);

  // Trigger an explicit refetch — wired to the Search button so users can
  // re-run the query without changing the date range. The DateRangeSelector
  // already calls `setRange` on Apply (which auto-fetches via effects), so
  // the Search button is most useful for retrying after a transient error
  // or after toggling the agent filter chip.
  const handleSearch = useCallback(() => {
    if (selectedClientKey != null) void fetchClientDetail(selectedClientKey);
    else void fetchOverview();
  }, [selectedClientKey, fetchClientDetail, fetchOverview]);

  // Filter row — date range + Search + (admin only) agent scope chip.
  // Mirrors the byDate tab's layout in `AnalyticsDashboardDialog`. In
  // embedded mode the back button (when drilled down) also lives here
  // since there is no separate title bar to host it.
  const filterRow = (
    <Box sx={FILTER_ROW_SX}>
      {embedded && selectedClientKey != null && (
        <Tooltip title="Back to overview">
          <IconButton size="small" onClick={handleBackToOverview} sx={{ mb: 0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <DateRangeSelector
        label="Date Range"
        range={range}
        onApply={setRange}
        presets={ANALYTICS_DATE_PRESETS}
        buttonMinWidth={240}
      />
      <Button
        variant="contained"
        startIcon={<SearchIcon />}
        onClick={handleSearch}
        disabled={overviewLoading || clientDetailLoading}
        sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
      >
        Search
      </Button>
      {isAdmin && (
        // Searchable agent picker. Replaces the previous "click row in Top
        // Agents to filter" interaction with an always-visible filter that
        // can also be cleared from the same control without scrolling.
        // The row-click in the Top Agents table is preserved as a
        // shortcut and updates this picker's value.
        <Autocomplete<TokenUsageAgentSummary, false, false, false>
          size="small"
          sx={{ minWidth: 320, mb: 0.5 }}
          value={selectedAgent}
          onChange={(_, value) => {
            setSelectedAgent(value);
            setSelectedClientKey(null);
            setClientDetail(null);
          }}
          inputValue={agentInput}
          onInputChange={(_, value) => setAgentInput(value)}
          options={agentOptions}
          loading={agentOptionsLoading}
          isOptionEqualToValue={(o, v) => o.ceaNumber === v.ceaNumber}
          getOptionLabel={(o) => o.name ? `${o.name} (${o.ceaNumber})` : o.ceaNumber}
          renderOption={(props, option) => (
            <li {...props} key={option.ceaNumber}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {option.name ?? '—'}
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                  {option.ceaNumber}
                  {option.tokensSpent > 0 ? ` · ${formatNumber(option.tokensSpent)} spent` : ''}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Filter by agent"
              placeholder="All agents"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {agentOptionsLoading ? <CircularProgress size={14} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    </Box>
  );

  const agentFilterKey = selectedAgent?.ceaNumber ?? '__all__';

  const body = (
    selectedClientKey != null
      ? renderClientDetail({
          loading: clientDetailLoading,
          error: clientDetailError,
          detail: clientDetail,
        })
      : (
        <AgentFilteredOverview agentFilterKey={agentFilterKey} loading={overviewLoading}>
          {renderOverview({
            loading: overviewLoading,
            error: overviewError,
            overview,
            search,
            setSearch,
            searchLoading,
            searchResults,
            onSelectClient: handleSelectClient,
            isAdmin,
            selectedAgentCea: selectedAgent?.ceaNumber ?? null,
            onSelectAgent: (a) => {
              setSelectedAgent(a);
              setSelectedClientKey(null);
              setClientDetail(null);
            },
          })}
        </AgentFilteredOverview>
      )
  );

  if (embedded) {
    // No title row — the parent dialog (AnalyticsDashboardDialog) already
    // shows "Analytics Dashboard" + the tab label, so a second "Token Usage
    // Analytics" heading would just be visual noise.
    return (
      <Stack spacing={2.5}>
        {filterRow}
        {body}
      </Stack>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedClientKey != null && (
              <Tooltip title="Back to overview">
                <IconButton size="small" onClick={handleBackToOverview}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedClientKey != null ? 'Client Token Usage' : 'Token Usage Analytics'}
            </Typography>
          </Box>
          {headerActions}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          {filterRow}
          {body}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

/** Fades overview content when the admin agent filter changes and dims while refetching. */
const AgentFilteredOverview: React.FC<{
  agentFilterKey: string;
  loading: boolean;
  children: React.ReactNode;
}> = ({ agentFilterKey, loading, children }) => (
  <Box sx={{ position: 'relative' }}>
    {loading && (
      <LinearProgress
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1,
          borderRadius: 1,
        }}
      />
    )}
    <Fade in appear timeout={280} key={agentFilterKey}>
      <Box
        sx={{
          opacity: loading ? 0.55 : 1,
          transition: 'opacity 0.25s ease',
          pointerEvents: loading ? 'none' : 'auto',
        }}
      >
        {children}
      </Box>
    </Fade>
  </Box>
);

function useTablePagination(itemCount: number) {
  const [page, setPage] = useState(0);
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(itemCount / ROWS_PER_PAGE) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [itemCount, page]);
  return { page, setPage };
}

const TablePaginationFooter: React.FC<{
  count: number;
  page: number;
  onPageChange: (page: number) => void;
}> = ({ count, page, onPageChange }) => (
  count > ROWS_PER_PAGE ? (
    <TablePagination
      component="div"
      count={count}
      page={page}
      onPageChange={(_, newPage) => onPageChange(newPage)}
      rowsPerPage={ROWS_PER_PAGE}
      rowsPerPageOptions={[ROWS_PER_PAGE]}
    />
  ) : null
);

const TopAgentsTable: React.FC<{
  agents: TokenUsageAgentSummary[];
  selectedAgentCea: string | null;
  onSelectAgent: (a: TokenUsageAgentSummary) => void;
}> = ({ agents, selectedAgentCea, onSelectAgent }) => {
  const { page, setPage } = useTablePagination(agents.length);
  const paginatedAgents = agents.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Agent</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>CEA Number</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Unique clients</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <TokenIcon sx={{ fontSize: 16, color: 'warning.main' }} /> Client Spent Tokens
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Last spend</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAgents.map((a) => (
              <TableRow
                key={a.ceaNumber}
                hover
                onClick={() => onSelectAgent(a)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: selectedAgentCea === a.ceaNumber ? 'rgba(237,137,54,0.08)' : undefined,
                }}
              >
                <TableCell>{a.name ?? '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{a.ceaNumber}</TableCell>
                <TableCell align="right">{formatNumber(a.totalClients)}</TableCell>
                <TableCell align="right">{formatNumber(a.tokensSpent)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTimestamp(a.lastSpendAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter count={agents.length} page={page} onPageChange={setPage} />
    </>
  );
};

// Renderers extracted as plain functions (not components) so the dialog
// state lives in one place and there's no extra hook indirection.

function renderOverview(args: {
  loading: boolean;
  error: string | null;
  overview: TokenUsageAdminOverviewResponse | null;
  search: string;
  setSearch: (v: string) => void;
  searchLoading: boolean;
  searchResults: TokenUsageClientSearchRow[] | null;
  onSelectClient: (clientKey: string) => void;
  /** Admin mode flag. Controls whether the Top Agents panel renders and
   *  whether the clients table surfaces the `Invited by` column. */
  isAdmin: boolean;
  /** Currently scoped agent (admin only). Highlights the matching row in
   *  the Top Agents panel. `null` ⇒ cross-agent view. */
  selectedAgentCea: string | null;
  /** Handler invoked when the admin clicks a Top Agents row, or `null` to
   *  clear the current scope from the panel header. */
  onSelectAgent: (a: TokenUsageAgentSummary | null) => void;
}): React.ReactNode {
  const {
    loading, error, overview, search, setSearch, searchLoading, searchResults,
    onSelectClient, isAdmin, selectedAgentCea, onSelectAgent,
  } = args;
  if (loading && !overview) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (!overview) {
    return <Typography>No data.</Typography>;
  }
  const { totals, sectionBreakdown, spendByDay, topProjects, topBlocks, topUnits, topClients, topAgents } = overview;
  return (
    <Stack spacing={2.5}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <HeadlineCard label="Tokens spent" value={formatNumber(totals.tokensSpent)} hint="In selected range" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <HeadlineCard
            label="Total unique clients"
            value={formatNumber(totals.totalClients)}
            hint={isAdmin ? 'Unique mobiles' : 'All-time invitations'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <HeadlineCard
            label="Avg per active client"
            value={totals.activeClients > 0 ? totals.avgTokensPerActiveClient.toFixed(2) : '—'}
            hint="Tokens / active client"
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Panel flex="1 1 360px">
          <PanelTitle>Section breakdown</PanelTitle>
          <SectionBreakdownPanel data={sectionBreakdown} />
        </Panel>
        <Panel flex="2 1 480px">
          <PanelTitle>Tokens / day</PanelTitle>
          <SimpleSparkline data={spendByDay} />
        </Panel>
      </Box>

      {/* Top Agents panel is only meaningful in the cross-agent view; once a
          single agent is selected the panel would collapse to one row, so we
          hide it entirely until the filter is cleared. */}
      {isAdmin && !selectedAgentCea && (
        <Panel flex="1 1 100%">
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
            <PanelTitle>Top agents</PanelTitle>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
              Click a row to filter the dashboard.
            </Typography>
          </Stack>
          {topAgents.length === 0 ? (
            <EmptyText>No agent activity in this period.</EmptyText>
          ) : (
            <TopAgentsTable
              agents={topAgents}
              selectedAgentCea={selectedAgentCea}
              onSelectAgent={onSelectAgent}
            />
          )}
        </Panel>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Panel>
          <PanelTitle>Top projects</PanelTitle>
          {topProjects.length === 0 ? (
            <EmptyText>No project spend in this period.</EmptyText>
          ) : (
            <SimpleTable
              cols={['Project', 'Tokens', 'Clients']}
              align={['left', 'right', 'right']}
              rows={topProjects.map((p) => [
                p.projectName ?? p.projectId,
                formatNumber(p.tokensSpent),
                formatNumber(p.uniqueClients),
              ])}
            />
          )}
        </Panel>
        <Panel>
          <PanelTitle>Top blocks</PanelTitle>
          {topBlocks.length === 0 ? (
            <EmptyText>No block-level spend in this period.</EmptyText>
          ) : (
            <SimpleTable
              cols={['Project', 'Block', 'Tokens']}
              align={['left', 'left', 'right']}
              rows={topBlocks.map((b) => [
                b.projectName ?? b.projectId,
                b.block,
                formatNumber(b.tokensSpent),
              ])}
            />
          )}
        </Panel>
        <Panel>
          <PanelTitle>Top units</PanelTitle>
          {topUnits.length === 0 ? (
            <EmptyText>No per-unit spend in this period.</EmptyText>
          ) : (
            <SimpleTable
              cols={['Project', 'Unit', 'Tokens']}
              align={['left', 'left', 'right']}
              rows={topUnits.map((u) => [
                u.projectName ?? u.projectId,
                u.unitKey,
                formatNumber(u.tokensSpent),
              ])}
            />
          )}
        </Panel>
      </Box>

      <Panel flex="1 1 100%">
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
          <PanelTitle>Clients</PanelTitle>
          <Box sx={{ flex: 1 }} />
          <TextField
            size="small"
            placeholder="Search by name or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={14} />
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Stack>

        <ClientsTable
          rows={(searchResults ?? topClients).map((row) => ({
            clientKey: row.clientKey,
            name: row.name,
            mobile: row.mobile,
            module: row.module,
            tokensSpent: 'tokensSpent' in row ? (row as { tokensSpent: number }).tokensSpent : null,
            remainingTokens: row.remainingTokens,
            maxTokens: row.maxTokens,
            shareLinkCount: row.shareLinkCount,
            lastSpendAt:
              'lastSpendAt' in row ? (row as { lastSpendAt: string | null }).lastSpendAt : null,
            createdAt: row.createdAt,
          }))}
          onSelect={onSelectClient}
          // When a search query is active we hide the spend column —
          // the search endpoint returns lightweight summaries and does
          // not include `tokensSpent`. The drill-down still has it.
          showSpendColumn={searchResults == null}
        />
      </Panel>
    </Stack>
  );
}

function renderClientDetail(args: {
  loading: boolean;
  error: string | null;
  detail: TokenUsageClientDetailResponse | null;
}): React.ReactNode {
  const { loading, error, detail } = args;
  if (loading && !detail) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!detail) return <Typography>No data.</Typography>;
  const { client, sectionBreakdown, spendByDay, projectBreakdown, blockBreakdown, events } = detail;
  return (
    <Stack spacing={2.5}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={chartCardSx}>
            <CardContent sx={{ p: '0 !important' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-heading)', mb: 1 }}>
                {client.name ?? '(no name)'}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <KeyValue label="Phone" value={client.mobile} />
                <KeyValue label="Module" value={formatModule(client.module)} />
                <KeyValue label="Project" value={client.projectId ?? '—'} />
                <KeyValue label="Activated" value={formatTimestamp(client.activatedAt)} />
                <KeyValue label="Created" value={formatTimestamp(client.createdAt)} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <HeadlineCard label="Tokens spent" value={formatNumber(client.tokensSpent)} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <HeadlineCard
            label="Remaining"
            value={`${formatNumber(client.remainingTokens)} / ${formatNumber(client.maxTokens)}`}
            hint="Remaining / Max"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <HeadlineCard label="Last spend" value={formatTimestamp(client.lastSpendAt)} />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Panel flex="1 1 320px">
          <PanelTitle>Section breakdown</PanelTitle>
          <SectionBreakdownPanel data={sectionBreakdown} />
        </Panel>
        <Panel flex="2 1 480px">
          <PanelTitle>Tokens / day</PanelTitle>
          <SimpleSparkline data={spendByDay} />
        </Panel>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Panel>
          <PanelTitle>Projects</PanelTitle>
          {projectBreakdown.length === 0 ? (
            <EmptyText>No project spend.</EmptyText>
          ) : (
            <SimpleTable
              cols={['Project', 'Tokens']}
              align={['left', 'right']}
              rows={projectBreakdown.map((p) => [
                p.projectName ?? p.projectId,
                formatNumber(p.tokensSpent),
              ])}
            />
          )}
        </Panel>
        <Panel>
          <PanelTitle>Blocks</PanelTitle>
          {blockBreakdown.length === 0 ? (
            <EmptyText>No block-level spend.</EmptyText>
          ) : (
            <SimpleTable
              cols={['Project', 'Block', 'Tokens']}
              align={['left', 'left', 'right']}
              rows={blockBreakdown.map((b) => [
                b.projectName ?? b.projectId,
                b.block,
                formatNumber(b.tokensSpent),
              ])}
            />
          )}
        </Panel>
      </Box>

      <Panel flex="1 1 100%">
        <PanelTitle>Recent activity</PanelTitle>
        {events.length === 0 ? (
          <EmptyText>No reveals recorded for this client in this date range.</EmptyText>
        ) : (
          <RecentActivityTable events={events} />
        )}
      </Panel>
    </Stack>
  );
}

// ── Small reusable bits ─────────────────────────────────────────────

const KeyValue: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {value}
    </Typography>
  </Box>
);

const EmptyText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
    {children}
  </Typography>
);

interface SimpleTableProps {
  cols: string[];
  align?: Array<'left' | 'right' | 'center'>;
  rows: Array<Array<string | number>>;
}
const SimpleTable: React.FC<SimpleTableProps> = ({ cols, align = [], rows }) => {
  const { page, setPage } = useTablePagination(rows.length);
  const paginatedRows = rows.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {cols.map((c, idx) => (
                <TableCell key={c} align={align[idx] ?? 'left'} sx={{ fontWeight: 600 }}>
                  {c}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row, idx) => (
              <TableRow key={page * ROWS_PER_PAGE + idx}>
                {row.map((cell, cIdx) => (
                  <TableCell key={cIdx} align={align[cIdx] ?? 'left'}>
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter count={rows.length} page={page} onPageChange={setPage} />
    </>
  );
};

interface ClientsTableRow {
  clientKey: string;
  name: string | null;
  mobile: string;
  module: string;
  tokensSpent: number | null;
  remainingTokens: number;
  maxTokens: number;
  /** Number of underlying share links collapsed into this row. Shown as
   *  a small badge so users know when one mobile has multiple invitations. */
  shareLinkCount: number;
  lastSpendAt: string | null;
  createdAt: string | null;
}

interface ClientsTableProps {
  rows: ClientsTableRow[];
  onSelect: (clientKey: string) => void;
  /** Hide the Spent column when the rows came from `searchResults` (the
   * search endpoint returns lightweight summaries without spend totals). */
  showSpendColumn: boolean;
}
const ClientsTable: React.FC<ClientsTableProps> = ({ rows, onSelect, showSpendColumn }) => {
  const { page, setPage } = useTablePagination(rows.length);
  const paginatedRows = rows.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
              {showSpendColumn && (
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <TokenIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    Spent
                  </Box>
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: 600 }} align="right">Remaining / Max</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{showSpendColumn ? 'Last spend' : 'Created'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showSpendColumn ? 6 : 5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No clients found.
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row) => (
                <TableRow
                  key={row.clientKey}
                  hover
                  onClick={() => onSelect(row.clientKey)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <span>{row.name ?? '—'}</span>
                      {row.shareLinkCount > 1 && (
                        <Chip
                          size="small"
                          label={`${row.shareLinkCount} links`}
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            bgcolor: 'rgba(var(--color-text-rgb), 0.08)',
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{row.mobile}</TableCell>
                  <TableCell>{formatModule(row.module)}</TableCell>
                  {showSpendColumn && (
                    <TableCell align="right">
                      {row.tokensSpent != null ? formatNumber(row.tokensSpent) : '—'}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {formatNumber(row.remainingTokens)} / {formatNumber(row.maxTokens)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {showSpendColumn ? formatTimestamp(row.lastSpendAt) : formatDate(row.createdAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter count={rows.length} page={page} onPageChange={setPage} />
    </>
  );
};

const RecentActivityTable: React.FC<{
  events: TokenUsageClientDetailResponse['events'];
}> = ({ events }) => {
  const { page, setPage } = useTablePagination(events.length);
  const paginatedEvents = events.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Block</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedEvents.map((ev) => (
              <TableRow key={ev.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {formatTimestamp(ev.createdAt)}
                </TableCell>
                <TableCell>{ev.projectName ?? ev.projectId ?? '—'}</TableCell>
                <TableCell>{ev.block ?? '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                  {ev.unitKey ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter count={events.length} page={page} onPageChange={setPage} />
    </>
  );
};

export default TokenUsageAnalyticsDialog;
